/**
 * Presence as the editor shows it (PLAN §6.6): the server's member list
 * (`presence.state`, one entry per connection) reduced to who else is here —
 * one avatar per user, not per tab — and which rows they are on.
 *
 * The current user is left out by user id: their own other tabs are not
 * "someone else". A user on several rows (two tabs) shows on each, and an
 * editing tab outranks a viewing one for the avatar's activity.
 */
import type { PresenceMemberWire } from '@acp/api-contract/ws.js';

/** How many distinct avatar colours there are (`--presence-1` … in globals.css). */
export const PRESENCE_COLOURS = 4;

export type PresenceActivity = 'viewing' | 'editing';

export type PresenceUser = {
  readonly userId: string;
  readonly displayName: string;
  readonly initials: string;
  /** 0-based index into the presence colour tokens, stable per user. */
  readonly colour: number;
  readonly activity: PresenceActivity;
};

export type RowPresence = {
  readonly userId: string;
  readonly displayName: string;
  readonly initials: string;
  readonly colour: number;
  readonly activity: PresenceActivity;
  readonly field: string | null;
};

export type PresenceView = {
  readonly users: readonly PresenceUser[];
  readonly byRow: ReadonlyMap<string, readonly RowPresence[]>;
};

export const EMPTY_PRESENCE: PresenceView = { users: [], byRow: new Map() };

/** Up to two letters: the first of the first two words of the name's local part (`anna.berg@x` → `AB`). */
export function initialsOf(displayName: string): string {
  const local = displayName.split('@')[0] ?? displayName;
  const words = local.split(/[\s._-]+/).filter((w) => w !== '');
  const letters = words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
  return letters === '' ? '?' : letters;
}

/** A stable colour per user id (FNV-1a), so a user keeps their colour across reloads and screens. */
export function colourOf(userId: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < userId.length; i += 1) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % PRESENCE_COLOURS;
}

export function presenceView(members: readonly PresenceMemberWire[], selfUserId: string): PresenceView {
  const users = new Map<string, PresenceUser>();
  const byRow = new Map<string, RowPresence[]>();
  for (const m of members) {
    if (m.userId === selfUserId) continue;
    const colour = colourOf(m.userId);
    const initials = initialsOf(m.displayName);
    const known = users.get(m.userId);
    // An activity from a newer client (D37) claims nothing: shown as viewing.
    const own: PresenceActivity = m.state.activity === 'unknown' ? 'viewing' : m.state.activity;
    const activity: PresenceActivity = known?.activity === 'editing' ? 'editing' : own;
    users.set(m.userId, { userId: m.userId, displayName: m.displayName, initials, colour, activity });
    const rowId = m.state.rowId;
    if (rowId === null) continue;
    const list = byRow.get(rowId) ?? [];
    if (!list.some((p) => p.userId === m.userId && p.activity === own && p.field === m.state.field)) {
      list.push({ userId: m.userId, displayName: m.displayName, initials, colour, activity: own, field: m.state.field });
    }
    byRow.set(rowId, list);
  }
  return { users: [...users.values()].sort((a, b) => a.displayName.localeCompare(b.displayName)), byRow };
}
